import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  eachMonthOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowDownCircle, ArrowUpCircle, Clock, AlertTriangle, CalendarClock } from 'lucide-react';
import { useFinanceEntriesRaw, useFinanceCategories } from '@/hooks/useFinance';
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';
import { DUE_SOON_DAYS, summarizeFinanceMonth } from '@/lib/financeOverview';
import { formatBRL, formatBRLShort, STATUS_LABELS } from '@/lib/financeLabels';
import { MONEY_AXIS_WIDTH, moneyAxisTick } from '@/components/reports/axisFormat';
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { cn } from '@/lib/utils';
import type { FinanceEntryWithStatus } from '@/types/database';

interface Props {
  onEdit: (entry: FinanceEntryWithStatus) => void;
}

const CHART_MONTHS = 6;

export function FinanceOverviewTab({ onEdit }: Props) {
  const { entries } = useFinanceEntriesRaw();
  const { categories } = useFinanceCategories();

  const model = useMemo(() => {
    const now = new Date();

    /**
     * Os KPIs saem da MESMA função que alimenta o hero (`summarizeFinanceMonth`).
     * Recalcular aqui faria o cartão e o número em `text-3xl` logo acima dele
     * discordarem na primeira correção de um dos dois, sem erro nenhum
     * (#82/#69). É também por isso que "Saldo do mês" não é mais um cartão: ele
     * é o valor do hero, e repeti-lo dois centímetros abaixo não informa nada —
     * o lugar dele ficou com "A pagar (7d)", que espelha o "A receber (7d)" e
     * não existia.
     */
    const month = summarizeFinanceMonth(entries, now);

    // Bar chart: last 6 months income vs expense (by due_date).
    const months = eachMonthOfInterval({
      start: startOfMonth(subMonths(now, CHART_MONTHS - 1)),
      end: endOfMonth(now),
    });
    const byMonth = months.map((m) => {
      const s = todayIsoDate(startOfMonth(m));
      const en = todayIsoDate(endOfMonth(m));
      let income = 0;
      let expense = 0;
      for (const e of entries) {
        if (e.due_date < s || e.due_date > en) continue;
        if (e.kind === 'income') income += Number(e.amount ?? 0);
        else expense += Number(e.amount ?? 0);
      }
      return { month: format(m, 'MMM', { locale: ptBR }), income, expense };
    });

    // Pie: expenses by category over the same 6-month window.
    const windowStart = todayIsoDate(startOfMonth(subMonths(now, CHART_MONTHS - 1)));
    const catName = new Map(categories.map((c) => [c.id, { name: c.name, color: c.color ?? '#64748b' }]));
    const byCatMap = new Map<string, { name: string; total: number; color: string }>();
    for (const e of entries) {
      if (e.kind !== 'expense') continue;
      if (e.due_date < windowStart) continue;
      const key = e.category_id ?? '__none__';
      const meta = e.category_id ? catName.get(e.category_id) : undefined;
      const prev = byCatMap.get(key) ?? {
        name: meta?.name ?? 'Sem categoria',
        total: 0,
        color: meta?.color ?? '#64748b',
      };
      prev.total += Number(e.amount ?? 0);
      byCatMap.set(key, prev);
    }
    const byCategory = Array.from(byCatMap.values()).sort((a, b) => b.total - a.total);

    const latest = [...entries]
      .sort((a, b) => (b.created_at ?? b.due_date).localeCompare(a.created_at ?? a.due_date))
      .slice(0, 5);

    return {
      month,
      byMonth,
      byCategory,
      latest,
      hasMonthlyData: byMonth.some((m) => m.income > 0 || m.expense > 0),
    };
  }, [entries, categories]);

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          icon={<ArrowUpCircle className="h-4 w-4 text-emerald-600" />}
          label="Recebido"
          value={formatBRLShort(model.month.received)}
          valueClass="text-emerald-600"
        />
        <KpiCard
          icon={<Clock className="h-4 w-4 text-amber-600" />}
          label={`A receber (${DUE_SOON_DAYS}d)`}
          value={formatBRLShort(model.month.dueSoonIncome)}
          valueClass="text-amber-600"
        />
        <KpiCard
          icon={<CalendarClock className="h-4 w-4 text-red-600" />}
          label={`A pagar (${DUE_SOON_DAYS}d)`}
          value={formatBRLShort(model.month.dueSoonExpense)}
          valueClass="text-red-600"
        />
        <KpiCard
          icon={<AlertTriangle className="h-4 w-4 text-red-600" />}
          label="Vencido"
          value={formatBRLShort(model.month.overdue)}
          valueClass="text-red-600"
        />
      </div>

      {/* Bar chart: fluxo mensal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fluxo de caixa (6 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {model.hasMonthlyData ? (
              <ResponsiveContainer width="100%" height="100%">
                {/* margin.left 0 + width explícito + tick sem centavos: com
                    `left: -10` e `formatBRLShort` cru, um tick "R$ 750,00" sai
                    com o "R$" CORTADO em 360px (#43). Não é bug que `tsc` ou
                    jsdom peguem — o texto existe, só está clipado pelo SVG. */}
                <BarChart data={model.byMonth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={moneyAxisTick}
                    width={MONEY_AXIS_WIDTH}
                    className="text-muted-foreground"
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      formatBRL(value),
                      name === 'income' ? 'Entradas' : 'Saídas',
                    ]}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend
                    formatter={(value) => (
                      <span className="text-sm">{value === 'income' ? 'Entradas' : 'Saídas'}</span>
                    )}
                  />
                  <Bar dataKey="income" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" stackId="b" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pie: despesas por categoria */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Despesas por categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {model.byCategory.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={model.byCategory}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="total"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {model.byCategory.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, name: string) => [formatBRL(value), name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Últimas movimentações */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Últimas movimentações</CardTitle>
        </CardHeader>
        <CardContent>
          {model.latest.length > 0 ? (
            <div className="space-y-1">
              {model.latest.map((e) => {
                const st = (e.computed_status as string) ?? computeFinanceStatus(e.status, e.due_date);
                return (
                  <button
                    key={e.id}
                    onClick={() => onEdit(e)}
                    className="flex w-full items-center justify-between gap-2 rounded-md py-2 text-left transition-colors hover:bg-muted/50 border-b last:border-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {e.kind === 'income' ? (
                        <ArrowUpCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <ArrowDownCircle className="h-4 w-4 shrink-0 text-red-600" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{e.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(localDateFromIsoDatePart(e.due_date), 'dd/MM/yyyy')} · {STATUS_LABELS[st as keyof typeof STATUS_LABELS] ?? st}
                        </p>
                      </div>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 text-sm font-semibold',
                        e.kind === 'income' ? 'text-emerald-600' : 'text-red-600',
                      )}
                    >
                      {e.kind === 'income' ? '+' : '-'}
                      {formatBRL(Number(e.amount ?? 0))}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhuma movimentação ainda.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  valueClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="mb-1 flex items-center gap-1.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">{icon}</div>
        </div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn('truncate text-base font-bold', valueClass)}>{value}</p>
      </CardContent>
    </Card>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
        <Clock className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">Sem dados no período</p>
    </div>
  );
}
